/**
 * FASWORLD search — modal, recent searches, recently viewed, Suggest API results
 */
(() => {
  if (window.__fwSearchInit) return;
  window.__fwSearchInit = true;

  const RECENT_KEY = 'fwRecentSearches';
  const VIEWED_KEY = 'viewedProducts';
  const RECENT_MAX = 8;
  const VIEWED_MAX = 4;
  const DEBOUNCE_MS = 220;

  const moneyFormat =
    (window.Shopify && window.Shopify.locale && window.theme?.moneyFormat) ||
    document.documentElement.getAttribute('data-money-format') ||
    null;

  const formatMoney = (cents) => {
    if (window.Shopify && typeof window.Shopify.formatMoney === 'function' && moneyFormat) {
      return window.Shopify.formatMoney(cents, moneyFormat);
    }
    const value = (Number(cents) || 0) / 100;
    try {
      return new Intl.NumberFormat(undefined, {
        style: 'currency',
        currency: window.Shopify?.currency?.active || 'QAR',
        currencyDisplay: 'code',
      }).format(value);
    } catch {
      return `QAR ${value.toFixed(2)}`;
    }
  };

  const modal = document.querySelector('[data-fw-search]');
  if (!modal) return;

  const readStrings = () => {
    const el = modal.querySelector('[data-fw-search-strings]');
    const fallback = {
      noResultsTemplate: 'No results for "__QUERY__"',
      viewAllResultsForTemplate: 'View all results for "__QUERY__"',
      searchForTemplate: 'Search for "__QUERY__"',
      genericError: 'Something went wrong. Try again.',
    };
    if (!el) return fallback;
    try {
      return { ...fallback, ...JSON.parse(el.textContent) };
    } catch {
      return fallback;
    }
  };

  const strings = readStrings();
  const withQuery = (template, query) => template.replace('__QUERY__', query);

  const input = modal.querySelector('[data-fw-search-input]');
  const clearBtn = modal.querySelector('[data-fw-search-clear]');
  const closeBtn = modal.querySelector('[data-fw-search-close]');
  const emptyPanel = modal.querySelector('[data-fw-search-empty]');
  const resultsPanel = modal.querySelector('[data-fw-search-results]');
  const statusEl = modal.querySelector('[data-fw-search-status]');
  const footer = modal.querySelector('[data-fw-search-footer]');
  const viewAll = modal.querySelector('[data-fw-search-view-all]');
  const recentSection = modal.querySelector('[data-fw-search-recent]');
  const recentList = modal.querySelector('[data-fw-search-recent-list]');
  const viewedSection = modal.querySelector('[data-fw-search-viewed]');
  const viewedList = modal.querySelector('[data-fw-search-viewed-list]');
  const hint = modal.querySelector('[data-fw-search-hint]');
  const querySection = modal.querySelector('[data-fw-search-queries]');
  const queryList = modal.querySelector('[data-fw-search-query-list]');
  const productSection = modal.querySelector('[data-fw-search-products]');
  const productList = modal.querySelector('[data-fw-search-product-list]');
  const collectionSection = modal.querySelector('[data-fw-search-collections]');
  const collectionList = modal.querySelector('[data-fw-search-collection-list]');
  const loader = modal.querySelector('[data-fw-search-loader]');
  const body = modal.querySelector('[data-fw-search-body]');

  let debounceTimer = null;
  let abortController = null;
  let lastFocus = null;

  const setLoading = (on) => {
    if (loader) loader.hidden = !on;
    modal.classList.toggle('is-loading', Boolean(on));
    if (body) body.setAttribute('aria-busy', on ? 'true' : 'false');
  };

  const readJson = (key, fallback) => {
    try {
      return JSON.parse(localStorage.getItem(key) || 'null') ?? fallback;
    } catch {
      return fallback;
    }
  };

  const writeJson = (key, value) => {
    localStorage.setItem(key, JSON.stringify(value));
  };

  const getRecent = () => readJson(RECENT_KEY, []);
  const setRecent = (items) => writeJson(RECENT_KEY, items.slice(0, RECENT_MAX));

  const pushRecent = (term) => {
    const clean = String(term || '').trim();
    if (clean.length < 2) return;
    const next = [clean, ...getRecent().filter((t) => t.toLowerCase() !== clean.toLowerCase())];
    setRecent(next);
  };

  const getViewedIds = () => readJson(VIEWED_KEY, []).slice(0, VIEWED_MAX);

  const escapeHtml = (value) =>
    String(value ?? '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');

  const productRow = (product) => {
    const image =
      product.image ||
      product.featured_image ||
      (product.featured_media && product.featured_media.preview_image?.src) ||
      '';
    const url = product.url || `#`;
    const title = product.title || 'Product';
    const price = product.price != null ? formatMoney(product.price) : '';
    const compare =
      product.compare_at_price_min != null && product.compare_at_price_min > product.price
        ? formatMoney(product.compare_at_price_min)
        : product.compare_at_price != null && product.compare_at_price > product.price
          ? formatMoney(product.compare_at_price)
          : '';

    return `<li class="fw-search__item">
      <a class="fw-search__product" href="${escapeHtml(url)}">
        <span class="fw-search__product-media">${
          image
            ? `<img src="${escapeHtml(image)}" alt="" loading="lazy" width="72" height="72">`
            : ''
        }</span>
        <span class="fw-search__product-meta">
          <span class="fw-search__product-title">${escapeHtml(title)}</span>
          <span class="fw-search__product-price">
            <span>${escapeHtml(price)}</span>
            ${compare ? `<s>${escapeHtml(compare)}</s>` : ''}
          </span>
        </span>
      </a>
    </li>`;
  };

  const renderRecent = () => {
    const items = getRecent();
    if (!items.length) {
      recentSection.hidden = true;
      recentList.innerHTML = '';
    } else {
      recentSection.hidden = false;
      recentList.innerHTML = items
        .map(
          (term) =>
            `<li><button type="button" class="fw-search__chip" data-fw-search-term="${escapeHtml(
              term
            )}">${escapeHtml(term)}</button></li>`
        )
        .join('');
    }
    updateHint();
  };

  const updateHint = () => {
    const hasContent = !recentSection.hidden || !viewedSection.hidden;
    if (hint) hint.hidden = hasContent;
  };

  const renderViewed = async () => {
    const ids = getViewedIds();
    if (!ids.length) {
      viewedSection.hidden = true;
      viewedList.innerHTML = '';
      updateHint();
      return;
    }

    const q = ids.map((id) => `id:${id}`).join(' OR ');
    try {
      const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(
        q
      )}&resources[type]=product&resources[limit]=${VIEWED_MAX}&resources[options][unavailable_products]=last`;
      const res = await fetch(url);
      if (!res.ok) throw new Error('viewed fetch failed');
      const data = await res.json();
      const products = data?.resources?.results?.products || [];
      if (!products.length) {
        viewedSection.hidden = true;
        viewedList.innerHTML = '';
      } else {
        viewedSection.hidden = false;
        viewedList.innerHTML = products.map(productRow).join('');
      }
    } catch {
      viewedSection.hidden = true;
      viewedList.innerHTML = '';
    }
    updateHint();
  };

  const showEmpty = () => {
    setLoading(false);
    emptyPanel.hidden = false;
    resultsPanel.hidden = true;
    footer.hidden = true;
    statusEl.hidden = true;
    renderRecent();
    renderViewed();
  };

  const showResultsShell = () => {
    emptyPanel.hidden = true;
    resultsPanel.hidden = false;
    resultsPanel.classList.remove('is-ready');
  };

  const renderResults = (data, query) => {
    const products = data?.resources?.results?.products || [];
    const collections = data?.resources?.results?.collections || [];
    const queries = data?.resources?.results?.queries || [];

    if (queries.length) {
      querySection.hidden = false;
      queryList.innerHTML = queries
        .map(
          (item) =>
            `<li><button type="button" class="fw-search__chip" data-fw-search-term="${escapeHtml(
              item.text
            )}">${escapeHtml(item.text)}</button></li>`
        )
        .join('');
    } else {
      querySection.hidden = true;
      queryList.innerHTML = '';
    }

    if (products.length) {
      productSection.hidden = false;
      productList.innerHTML = products.map(productRow).join('');
    } else {
      productSection.hidden = true;
      productList.innerHTML = '';
    }

    if (collections.length) {
      collectionSection.hidden = false;
      collectionList.innerHTML = collections
        .map(
          (c) =>
            `<li class="fw-search__item"><a class="fw-search__row" href="${escapeHtml(
              c.url
            )}"><span>${escapeHtml(c.title)}</span></a></li>`
        )
        .join('');
    } else {
      collectionSection.hidden = true;
      collectionList.innerHTML = '';
    }

    const hasAny = products.length || collections.length || queries.length;
    statusEl.hidden = hasAny;
    if (!hasAny) {
      statusEl.hidden = false;
      statusEl.textContent = withQuery(strings.noResultsTemplate, query);
    }

    footer.hidden = !query;
    if (viewAll) {
      viewAll.href = `${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(query)}`;
      viewAll.textContent = hasAny
        ? withQuery(strings.viewAllResultsForTemplate, query)
        : withQuery(strings.searchForTemplate, query);
    }

    window.requestAnimationFrame(() => {
      resultsPanel.classList.add('is-ready');
    });
  };

  const search = async (query) => {
    const q = String(query || '').trim();
    if (q.length < 2) {
      showEmpty();
      return;
    }

    showResultsShell();
    setLoading(true);
    statusEl.hidden = true;
    footer.hidden = false;
    if (viewAll) viewAll.href = `${window.Shopify?.routes?.root || '/'}search?q=${encodeURIComponent(q)}`;

    abortController?.abort();
    abortController = new AbortController();

    const url = `${window.Shopify?.routes?.root || '/'}search/suggest.json?q=${encodeURIComponent(
      q
    )}&resources[type]=product,collection,query&resources[limit]=8&resources[options][unavailable_products]=last&resources[options][fields]=title,product_type,variants.title,vendor,tag`;

    try {
      const res = await fetch(url, { signal: abortController.signal });
      if (!res.ok) throw new Error('search failed');
      const data = await res.json();
      setLoading(false);
      renderResults(data, q);
    } catch (error) {
      if (error.name === 'AbortError') return;
      setLoading(false);
      statusEl.hidden = false;
      statusEl.textContent = strings.genericError;
      resultsPanel.classList.add('is-ready');
    }
  };

  const open = () => {
    lastFocus = document.activeElement;
    if (typeof modal.showModal === 'function') modal.showModal();
    else modal.setAttribute('open', '');
    document.documentElement.classList.add('fw-search-open');
    showEmpty();
    window.setTimeout(() => input?.focus(), 20);
  };

  const close = () => {
    if (typeof modal.close === 'function') modal.close();
    else modal.removeAttribute('open');
    document.documentElement.classList.remove('fw-search-open');
    lastFocus?.focus?.();
  };

  document.querySelectorAll('[data-fw-search-open]').forEach((btn) => {
    btn.addEventListener('click', (event) => {
      event.preventDefault();
      open();
    });
  });

  closeBtn?.addEventListener('click', close);

  modal.addEventListener('click', (event) => {
    if (event.target === modal) close();
  });

  modal.addEventListener('close', () => {
    document.documentElement.classList.remove('fw-search-open');
  });

  document.addEventListener('keydown', (event) => {
    if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
      event.preventDefault();
      if (modal.open) close();
      else open();
    }
  });

  input?.addEventListener('input', () => {
    const value = input.value.trim();
    clearBtn.hidden = value.length === 0;
    window.clearTimeout(debounceTimer);
    debounceTimer = window.setTimeout(() => search(value), DEBOUNCE_MS);
  });

  clearBtn?.addEventListener('click', () => {
    input.value = '';
    clearBtn.hidden = true;
    showEmpty();
    input.focus();
  });

  modal.querySelector('[data-fw-search-form]')?.addEventListener('submit', () => {
    pushRecent(input.value);
  });

  modal.addEventListener('click', (event) => {
    const termBtn = event.target.closest('[data-fw-search-term]');
    if (termBtn) {
      const term = termBtn.getAttribute('data-fw-search-term') || '';
      input.value = term;
      clearBtn.hidden = false;
      pushRecent(term);
      search(term);
      return;
    }

    if (event.target.closest('[data-fw-search-clear-recent]')) {
      setRecent([]);
      renderRecent();
      return;
    }

    if (event.target.closest('[data-fw-search-clear-viewed]')) {
      localStorage.removeItem(VIEWED_KEY);
      renderViewed();
    }
  });

})();
